// GoalsPage — native Nimbus. First fully-migrated screen (was the migration pilot).
// Data/service logic is unchanged from the original; only the presentation layer is Nimbus.
import { useState, useEffect } from 'react'
import {
  Button, Tag, Card, Input, Label, Select, Modal, Textarea, Icon, Spinner, Title, Text, Box,
} from '@nimbus-ds/components'
import {
  PlusIcon, FlagIcon, TrashIcon, ChevronRightIcon, StatsIcon, GenerativeStarsIcon, DownloadIcon,
} from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import {
  goalService, BINDING_METRICS, formatMetricValue,
  type Goal, type Actionable, type Indicator, type Binding, type BindingKind,
  type IndicatorStatus, type ProposedGoal, type PlanDraft, type PlanDraftGoal,
} from '@/api/goals'
import { questionService } from '@/api/questions'
import { funnelService } from '@/api/funnel'
import { listCustomEvents, createCustomEvent, type CustomEventDefinition } from '@/api/customEvents'
import { cohortService, type CohortDefinition } from '@/api/cohorts'
import { provisionStarterPack } from '@/api/starterPack'
import { withGa4Baseline, cleanupProvisionedPrimitives } from '@/api/baseline'
import { authService } from '@/api/auth'
import type { FunnelStep } from '@/types'

const KIND_LABELS: Record<BindingKind, string> = {
  funnel_step: 'Paso del embudo',
  datalake_event: 'Evento (conteo)',
  custom_event: 'Evento personalizado',
  propensity: 'Propensión a comprar',
  cohort: 'Cohorte',
  store_funnel: 'Tienda (global)',
  product: 'Producto (SKU)',
  search: 'Buscador',
  visitor_segment: 'Segmento de visitantes',
  traffic_channel: 'Canal de tráfico',
}

// One-line explanation of what each metric source measures.
const KIND_DESCRIPTIONS: Record<BindingKind, string> = {
  funnel_step: 'Mide qué pasa en un paso del recorrido de compra (ej: ver producto, agregar al carrito).',
  datalake_event: 'Cuenta cuántas veces ocurrió un evento de la tienda. Solo conteo, sin detalle por visitante.',
  custom_event: 'Mide un evento que vos definiste (clics, scroll, ver imágenes). Incluye visitantes únicos.',
  propensity: 'Compara cuánto más (o menos) compran quienes hacen este evento.',
  cohort: 'Mide un grupo de visitantes según su comportamiento (ej: usaron mobile, abandonaron el carrito).',
  store_funnel: 'Métrica global de toda la tienda.',
  product: 'Mide un producto puntual: vistas, conversiones o ingresos (ej: verificar que un SKU arreglado vuelva a recibir vistas).',
  search: 'Mide el uso del buscador de la tienda: % de sesiones que buscan y conversión de quienes buscan.',
  visitor_segment: 'Mide la composición de visitas que muestra Resultados: rebotaron, navegaron, compraron con los ojos, compraron.',
  traffic_channel: 'Mide un canal de adquisición (Email, Paid Social, Organic Search…): su porción del tráfico y su conversión.',
}

// Human-readable Spanish names for every metric the bindings expose.
const METRIC_LABELS: Record<string, string> = {
  // funnel_step
  step_sessions: 'Sesiones que llegan al paso',
  step_unique_visitors: 'Visitantes únicos en el paso',
  step_conversion_rate: 'Tasa de conversión del paso',
  drop_off_rate: 'Tasa de abandono en el paso',
  // datalake_event
  occurrences: 'Cantidad de veces que ocurre',
  // custom_event
  total_occurrences: 'Cantidad total de veces',
  unique_visitors: 'Visitantes únicos',
  unique_sessions: 'Sesiones únicas',
  visitor_engagement_rate: '% de visitantes que lo hacen',
  session_engagement_rate: '% de sesiones donde ocurre',
  // propensity
  conversion_lift: 'Aumento de conversión (ratio)',
  conversion_lift_percentage: 'Aumento de conversión (%)',
  // cohort
  sessions: 'Sesiones',
  conversions: 'Conversiones',
  conversion_rate: 'Tasa de conversión',
  revenue: 'Ingresos',
  // store_funnel
  total_funnel_hits: 'Visitas totales',
  aov: 'Ticket promedio (AOV)',
  // product (conversions/revenue reuse the cohort labels above)
  views: 'Vistas del producto',
  // search
  usage_rate: '% de sesiones que buscan',
  searcher_conversion_rate: 'Conversión de quienes buscan',
  search_sessions: 'Sesiones con búsqueda',
  // visitor_segment
  share: '% del tráfico',
  bouncers_share: '% que rebota',
  browsers_share: '% que navega sin ver productos',
  shoppers_share: '% que compra con los ojos',
  buyers_share: '% que compra',
}

const metricLabel = (m: string) => METRIC_LABELS[m] || m

const COMMERCE_EVENTS = ['add_to_cart', 'cart_opened', 'checkout_initiated', 'purchase', 'pageview']
// Rate metrics are 0–1 fractions; an absolute target typed as "5" means 5% → store as 0.05.
const RATE_KINDS = new Set(['conversion_rate', 'step_conversion_rate', 'drop_off_rate', 'visitor_engagement_rate', 'session_engagement_rate', 'usage_rate', 'searcher_conversion_rate', 'bouncers_share', 'browsers_share', 'shoppers_share', 'buyers_share', 'share'])

const emptyIndicator = (): Indicator => ({
  label: '',
  binding: { kind: 'store_funnel', metric: 'conversion_rate' },
  direction: 'up_is_good',
  baseline: null,
  threshold: { watch_pct: 5, risk_pct: 15 },
})

interface PickerContext {
  storeId: string
  funnelSteps: FunnelStep[]
  customEvents: CustomEventDefinition[]
  cohorts: CohortDefinition[]
}

// Segment-key options per dimension (v1: the useful subset). A segmented goal binds to ONE cell.
const DEVICE_SEG_KEYS = [{ key: 'mobile', label: 'Celular' }, { key: 'desktop', label: 'Escritorio' }]
const ORIGIN_SEG_KEYS = [
  { key: 'google_paid', label: 'Google Ads' }, { key: 'meta_ads', label: 'Meta Ads' },
  { key: 'google', label: 'Google (orgánico)' }, { key: 'instagram', label: 'Instagram' }, { key: 'direct', label: 'Directo' },
]

function IndicatorEditor({ value, onChange, ctx, idPrefix }: {
  value: Indicator; onChange: (i: Indicator) => void; ctx: PickerContext; idPrefix: string
}) {
  const b = value.binding
  const setBinding = (patch: Partial<Binding>) => {
    const next = { ...b, ...patch }
    // When kind changes, reset metric to the first valid one and clear refs.
    if (patch.kind && patch.kind !== b.kind) {
      onChange({ ...value, binding: { kind: patch.kind, metric: BINDING_METRICS[patch.kind][0] } })
      return
    }
    onChange({ ...value, binding: next })
  }

  return (
    <div className="space-y-3 rounded-md border p-3 bg-muted/30">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor={`${idPrefix}-kind`}>Tipo de métrica</Label>
          <Select id={`${idPrefix}-kind`} name={`${idPrefix}-kind`} value={b.kind}
            onChange={(e) => setBinding({ kind: e.target.value as BindingKind })}>
            {Object.entries(KIND_LABELS).map(([k, label]) => (
              <Select.Option key={k} value={k} label={label} />
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-metric`}>Métrica</Label>
          <Select id={`${idPrefix}-metric`} name={`${idPrefix}-metric`} value={b.metric}
            onChange={(e) => setBinding({ metric: e.target.value })}>
            {BINDING_METRICS[b.kind].map((m) => <Select.Option key={m} value={m} label={metricLabel(m)} />)}
          </Select>
        </div>
      </div>

      <Text fontSize="caption">{KIND_DESCRIPTIONS[b.kind]}</Text>

      {/* Ref picker by kind */}
      {b.kind === 'funnel_step' && (
        <div>
          <Label htmlFor={`${idPrefix}-step`}>Paso</Label>
          <Select id={`${idPrefix}-step`} name={`${idPrefix}-step`} value={b.step_id || ''}
            onChange={(e) => setBinding({ step_id: e.target.value, funnel_id: `funnel_default_${ctx.storeId}` })}>
            <Select.Option value="" label="Elegí un paso" />
            {ctx.funnelSteps.map((s) => <Select.Option key={s.id} value={s.id} label={s.name} />)}
          </Select>
        </div>
      )}
      {b.kind === 'datalake_event' && (
        <div>
          <Label htmlFor={`${idPrefix}-event`}>Evento</Label>
          <Select id={`${idPrefix}-event`} name={`${idPrefix}-event`} value={b.event_name || ''}
            onChange={(e) => setBinding({ event_name: e.target.value })}>
            <Select.Option value="" label="Elegí un evento" />
            {COMMERCE_EVENTS.map((ev) => <Select.Option key={ev} value={ev} label={ev} />)}
          </Select>
        </div>
      )}
      {b.kind === 'product' && (
        <div>
          <Label htmlFor={`${idPrefix}-pid`}>ID del producto</Label>
          <Input id={`${idPrefix}-pid`} value={b.product_id || ''}
            onChange={(e) => setBinding({ product_id: e.target.value.trim() })}
            placeholder="ej: 123456789 (ID de TiendaNube)" />
        </div>
      )}
      {b.kind === 'traffic_channel' && (
        <div>
          <Label htmlFor={`${idPrefix}-channel`}>Canal</Label>
          <Select id={`${idPrefix}-channel`} name={`${idPrefix}-channel`} value={b.channel || ''}
            onChange={(e) => setBinding({ channel: e.target.value })}>
            <Select.Option value="" label="Elegí un canal" />
            {['Email', 'Paid Search', 'Paid Social', 'Organic Search', 'Organic Social', 'Referral', 'Paid Other', 'Direct'].map((c) => (
              <Select.Option key={c} value={c} label={c} />
            ))}
          </Select>
        </div>
      )}
      {(b.kind === 'custom_event' || b.kind === 'propensity') && (
        <div>
          <Label htmlFor={`${idPrefix}-ce`}>Evento personalizado</Label>
          <Select id={`${idPrefix}-ce`} name={`${idPrefix}-ce`} value={b.event_id || ''}
            onChange={(e) => setBinding({ event_id: e.target.value })}>
            <Select.Option value="" label="Elegí un evento" />
            {ctx.customEvents.map((ev) => (
              <Select.Option key={ev.event_id} value={ev.event_id} label={ev.display_name || ev.event_name} />
            ))}
          </Select>
        </div>
      )}
      {b.kind === 'cohort' && (
        <div>
          <Label htmlFor={`${idPrefix}-cohort`}>Cohorte</Label>
          <Select id={`${idPrefix}-cohort`} name={`${idPrefix}-cohort`} value={b.cohort_id || ''}
            onChange={(e) => setBinding({ cohort_id: e.target.value })}>
            <Select.Option value="" label="Elegí una cohorte" />
            {ctx.cohorts.map((c) => <Select.Option key={c.cohort_id} value={c.cohort_id} label={c.name} />)}
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor={`${idPrefix}-label`}>Etiqueta</Label>
          <Input id={`${idPrefix}-label`} value={value.label} placeholder="Ej: Inicios de checkout"
            onChange={(e) => onChange({ ...value, label: e.target.value })} />
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-dir`}>Dirección</Label>
          <Select id={`${idPrefix}-dir`} name={`${idPrefix}-dir`} value={value.direction}
            onChange={(e) => onChange({ ...value, direction: e.target.value as Indicator['direction'] })}>
            <Select.Option value="up_is_good" label="Subir es bueno" />
            <Select.Option value="down_is_good" label="Bajar es bueno" />
          </Select>
        </div>
      </div>
      <div className="mt-3">
        <Label htmlFor={`${idPrefix}-seg`}>Segmento (opcional)</Label>
        <div className="flex gap-2">
          <Select id={`${idPrefix}-seg`} name={`${idPrefix}-seg`} value={value.segment?.dimension || 'none'}
            onChange={(e) => { const d = e.target.value; onChange({ ...value, segment: d === 'none' ? undefined : { dimension: d as 'origin' | 'device', key: d === 'device' ? 'mobile' : 'google_paid' } }) }}>
            <Select.Option value="none" label="Toda la tienda" />
            <Select.Option value="device" label="Dispositivo" />
            <Select.Option value="origin" label="Origen" />
          </Select>
          {value.segment && (
            <Select id={`${idPrefix}-segkey`} name={`${idPrefix}-segkey`} value={value.segment.key}
              onChange={(e) => onChange({ ...value, segment: { dimension: value.segment!.dimension, key: e.target.value } })}>
              {(value.segment.dimension === 'device' ? DEVICE_SEG_KEYS : ORIGIN_SEG_KEYS).map((o) => (
                <Select.Option key={o.key} value={o.key} label={o.label} />
              ))}
            </Select>
          )}
        </div>
        {value.segment && (
          <p className="mt-1 text-xs text-amber-600">
            Objetivo segmentado: instrumento de consultoría — no se muestra al comerciante hasta que podamos controlar su mezcla de tráfico (v2).
          </p>
        )}
      </div>
      <div className="mt-3">
        <Label htmlFor={`${idPrefix}-nivel`}>Nivel de binding (¿qué lectura causal soporta?)</Label>
        <Select id={`${idPrefix}-nivel`} name={`${idPrefix}-nivel`} value={value.nivel_binding || 'none'}
          onChange={(e) => { const n = e.target.value; onChange({ ...value, nivel_binding: n === 'none' ? undefined : (n as Indicator['nivel_binding']) }) }}>
          <Select.Option value="none" label="Sin declarar (sin veredicto por accionable)" />
          <Select.Option value="N4" label="N4 — Experimental (A/B: hay contrafáctico)" />
          <Select.Option value="N3" label="N3 — Atribuible (llave: UTM, cupón, SKU, evento)" />
          <Select.Option value="N2" label="N2 — De mecanismo (paso proximal del cambio)" />
          <Select.Option value="N1" label="N1 — Direccional / global (se lee en la meta)" />
          <Select.Option value="N0" label="N0 — No bindeable (observación, sin lectura)" />
        </Select>
        {(value.nivel_binding === 'N0' || value.nivel_binding === 'N1') && (
          <p className="mt-1 text-xs text-muted-foreground">
            Este nivel no emite veredicto por accionable: el cambio se ejecuta igual, pero su efecto no se narra como causa. ¿Cómo sabrías que fue esto y no otra cosa? Ese indicador es el de mecanismo.
          </p>
        )}
        {value.nivel_binding === 'N2' && !value.segment && (
          <p className="mt-1 text-xs text-amber-600">
            N2 sin alcance declarado: si el cambio no es de toda la tienda, elegí el segmento expuesto (arriba) para leerlo en su contexto.
          </p>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ goal }: { goal: Goal }) {
  const label: Record<string, string> = {
    calibrating: 'Calibrando', on_track: 'En camino', watch: 'Atención',
    at_risk: 'En riesgo', achieved: 'Logrado',
  }
  // Map the goal status light to a Nimbus Tag appearance.
  const appearance = goal.status === 'at_risk' ? 'danger'
    : goal.status === 'watch' ? 'warning'
    : goal.status === 'on_track' || goal.status === 'achieved' ? 'success'
    : 'neutral'
  return <Tag appearance={appearance}>{goal.status_light} {label[goal.status] || goal.status}</Tag>
}

// Consultant-facing signal: the posterior probability (rate) or pace (count).
function consultantSignal(c?: IndicatorStatus): { txt: string; cls: string } | null {
  if (!c) return null
  if (c.p_worse != null) {
    if (c.p_worse >= 0.8) return { txt: `P(peor) ${c.p_worse.toFixed(2)}`, cls: 'text-red-600' }
    if ((c.p_better ?? 0) >= 0.8) return { txt: `P(mejor) ${(c.p_better ?? 0).toFixed(2)}`, cls: 'text-green-600' }
    return { txt: 'estable', cls: 'text-muted-foreground' }
  }
  if (c.cumulative != null && c.target != null) {
    // Pace headline (where are you) + forward-rate verdict (will you make it).
    const head = `${Math.round(c.cumulative).toLocaleString()}/${Math.round(c.target).toLocaleString()}`
    const onPace = c.pace == null || c.pace >= 1
    const rate = c.pace != null ? ` · ritmo ${c.pace.toFixed(2)}` : ''
    return { txt: `${head}${rate}`, cls: c.achieved || onPace ? 'text-green-600' : c.breached ? 'text-red-600' : 'text-amber-600' }
  }
  if (c.pace != null) return { txt: `ritmo ${c.pace.toFixed(2)}`, cls: c.pace >= 1 ? 'text-green-600' : 'text-red-600' }
  if (c.state === 'informational') return { txt: 'informativo', cls: 'text-muted-foreground' }
  return null
}

function IndicatorRow({ ind }: { ind: Indicator }) {
  const c = ind.current
  const sig = consultantSignal(c)
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="flex items-center gap-2">
        <span>{c?.light || '⏳'}</span>
        <span>{ind.label || metricLabel(ind.binding.metric)}</span>
      </span>
      <span className="text-muted-foreground">
        {formatMetricValue(ind.binding?.metric, c?.value)}
        {sig && <span className={`ml-2 ${sig.cls}`}>{sig.txt}</span>}
        {c?.mix && c.mix.disposition !== 'store-red' && (
          <span className="ml-2 text-amber-600"
            title={c.mix.disposition === 'traffic'
              ? `${c.mix.annotate} (${c.mix.dimension}, residuo ${c.mix.residual ?? '—'})`
              : 'Cobertura insuficiente para atribuir — revisá el origen.'}>
            {c.mix.disposition === 'traffic' ? `⚑ tráfico (${c.mix.dimension})` : '⚑ revisar origen'}
          </span>
        )}
      </span>
    </div>
  )
}

export function GoalsPage() {
  const { toast } = useToast()
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)
  const [ctx, setCtx] = useState<PickerContext>({ storeId: '', funnelSteps: [], customEvents: [], cohorts: [] })
  const [openCreate, setOpenCreate] = useState(false)
  const [openAssistant, setOpenAssistant] = useState(false)
  const [selected, setSelected] = useState<Goal | null>(null)
  const [pickersLoaded, setPickersLoaded] = useState(false)
  const [provisioning, setProvisioning] = useState(false)
  // Plan drafts (put_plan_draft gate): pending drafts from meetings, applied/dismissed HERE.
  const [drafts, setDrafts] = useState<PlanDraft[]>([])
  const [reviewing, setReviewing] = useState<PlanDraft | null>(null)

  // Create-goal form state
  const [gName, setGName] = useState('')
  const [gDesc, setGDesc] = useState('')
  const [gMetric, setGMetric] = useState<Indicator>(emptyIndicator())
  const [gTarget, setGTarget] = useState('')
  const [gTargetType, setGTargetType] = useState<'relative_pct' | 'absolute'>('relative_pct')
  const [gTier, setGTier] = useState<'high' | 'medium' | 'low'>('medium')

  const storeId = authService.getCurrentStoreId() || ''

  const loadGoals = async () => {
    setLoading(true)
    try {
      const res = await goalService.listGoals(7)
      setGoals(res.goals)
    } catch (e: any) {
      toast({ title: 'Error al cargar objetivos', description: e?.message, variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const loadDrafts = async () => {
    try {
      const pending = await goalService.listPlanDrafts('pending', true)
      setDrafts(pending)
      // Deep-link from put_plan_draft's review_url: /goals?draft=<id> opens the gate directly.
      const wanted = new URLSearchParams(window.location.search).get('draft')
      if (wanted) {
        const d = pending.find(x => x.draft_id === wanted)
        if (d) setReviewing(d)
      }
    } catch { /* drafts are additive — the page works without them */ }
  }
  useEffect(() => { if (storeId) loadDrafts() }, [storeId])

  // Brief de ejecución del merchant: descarga el HTML para que la consultora lo REVISE
  // antes de compartirlo (el gate mecánico ya filtró; esta es la segunda barrera).
  const [briefLoading, setBriefLoading] = useState(false)
  const handleBrief = async () => {
    setBriefLoading(true)
    try {
      const brief = await goalService.getBrief()
      const url = URL.createObjectURL(new Blob([brief.html], { type: 'text/html' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `plan-de-trabajo-${brief.store_id}-${brief.generated_at.slice(0, 10)}.html`
      a.click()
      URL.revokeObjectURL(url)
      toast({ title: 'Brief generado', description: 'Revisalo antes de compartirlo con el merchant.' })
    } catch (e: any) {
      toast({ title: 'Error al generar el brief', description: e?.message, variant: 'destructive' })
    } finally {
      setBriefLoading(false)
    }
  }

  const loadPickers = async () => {
    try {
      const [funnel, ce, co] = await Promise.all([
        funnelService.getFunnelConfig(storeId).catch(() => ({ steps: [] as FunnelStep[] })),
        listCustomEvents(storeId, 'active').catch(() => ({ custom_events: [] as CustomEventDefinition[], count: 0 })),
        cohortService.listCohorts('active').catch(() => [] as CohortDefinition[]),
      ])
      setCtx({ storeId, funnelSteps: (funnel as any).steps || [], customEvents: ce.custom_events || [], cohorts: co })
    } catch { /* pickers are best-effort */ }
    finally { setPickersLoaded(true) }
  }

  const handleInstallStarter = async () => {
    setProvisioning(true)
    try {
      const r = await provisionStarterPack(storeId)
      toast({ title: 'Paquete inicial instalado', description: `${r.cohortsCreated} cohortes y ${r.eventsCreated} evento(s) creados.` })
      await loadPickers()
    } catch (e: any) {
      toast({ title: 'Error al instalar', description: e?.message, variant: 'destructive' })
    } finally { setProvisioning(false) }
  }

  useEffect(() => {
    loadGoals()
    loadPickers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCreate = async () => {
    if (!gName) {
      toast({ title: 'Falta el nombre', description: 'Poné un nombre al objetivo.', variant: 'destructive' })
      return
    }
    const labeled = { ...gMetric, label: gMetric.label || metricLabel(gMetric.binding.metric) }
    // Try an instant GA4 baseline (store-level metrics); else calibration window.
    const primary_metric = await withGa4Baseline(labeled, storeId)
    const targetNum = parseFloat(gTarget)
    const target = !isNaN(targetNum)
      ? { value: gTargetType === 'absolute' && RATE_KINDS.has(gMetric.binding.metric) ? targetNum / 100 : targetNum, type: gTargetType }
      : null
    try {
      await goalService.createGoal({ name: gName, description: gDesc, primary_metric, target, impact_tier: gTier })
      toast({ title: 'Objetivo creado' })
      setOpenCreate(false); setGName(''); setGDesc(''); setGMetric(emptyIndicator()); setGTarget(''); setGTier('medium')
      loadGoals()
    } catch (e: any) {
      toast({ title: 'Error al crear', description: e?.message, variant: 'destructive' })
    }
  }

  const handleArchive = async (goal: Goal) => {
    if (!confirm(`¿Archivar el objetivo "${goal.name}"?`)) return
    await goalService.archiveGoal(goal.goal_id)
    // Free assistant-provisioned cohorts/events nothing else uses (iteration hygiene).
    const cleaned = await cleanupProvisionedPrimitives(goal.goal_id).catch(() => null)
    const freed = cleaned ? cleaned.cohortsArchived + cleaned.eventsArchived : 0
    toast({ title: 'Objetivo archivado', description: freed ? `${freed} cohorte(s)/evento(s) sin uso archivado(s).` : undefined })
    loadGoals()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Icon source={<FlagIcon />} /> <Title as="h1">Objetivos</Title>
          </div>
          <Text>Objetivos de la iteración y sus indicadores de seguimiento.</Text>
        </div>
        <div className="flex gap-2">
          <Button appearance="neutral" onClick={handleBrief} disabled={briefLoading}>
            <Icon source={<DownloadIcon />} color="currentColor" /> {briefLoading ? 'Generando…' : 'Generar brief'}
          </Button>
          <Button appearance="neutral" onClick={() => setOpenAssistant(true)}>
            <Icon source={<GenerativeStarsIcon />} color="currentColor" /> Asistente IA
          </Button>
          <Button appearance="primary" onClick={() => setOpenCreate(true)}>
            <Icon source={<PlusIcon />} color="currentColor" /> Nuevo objetivo
          </Button>
        </div>
      </div>

      {pickersLoaded && ctx.cohorts.length === 0 && ctx.customEvents.length === 0 && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Text color="neutral-textHigh">Instalá el paquete inicial recomendado</Text>
                <Text>
                  Crea cohortes universales (mobile/desktop, abandono de carrito, convertidores) y un evento de engagement,
                  para que la tienda empiece a juntar datos desde ya.
                </Text>
              </div>
              <Box flex="0 0 auto">
                <Button appearance="primary" onClick={handleInstallStarter} disabled={provisioning}>
                  {provisioning ? <><Spinner size="small" /> Instalando…</> : <>Instalar</>}
                </Button>
              </Box>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Plan drafts pending the consultant's gate (put_plan_draft): reported, never silent */}
      {drafts.length > 0 && (
        <Card>
          <Card.Body>
            <div className="space-y-2">
              <Text color="neutral-textHigh">
                📋 {drafts.length} borrador(es) de plan esperando tu revisión — nada se activa hasta que apliques.
              </Text>
              {drafts.map((d) => (
                <div key={d.draft_id} className="flex items-center justify-between gap-4">
                  <Text fontSize="caption">
                    {new Date(d.created_at).toLocaleDateString('es-AR')}
                    {d.meeting_context ? ` · ${d.meeting_context}` : ''}
                    {' · '}{(d.plan_doc?.goals || []).length} metas, {(d.plan_doc?.goals || []).reduce((n, g) => n + (g.actionables || []).length, 0)} accionables, {(d.plan_doc?.preguntas || []).length} preguntas
                  </Text>
                  <Box flex="0 0 auto">
                    <Button appearance="primary" onClick={() => setReviewing(d)}>Revisar</Button>
                  </Box>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {loading ? (
        <Text>Cargando…</Text>
      ) : goals.length === 0 ? (
        <Card><Card.Body>
          <Text className="py-10 text-center">
            Todavía no hay objetivos. Creá el primero para empezar a monitorear.
          </Text>
        </Card.Body></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {goals.map((goal) => (
            <div key={goal.goal_id} className="cursor-pointer"
              onClick={() => { setSelected(goal); goalService.getGoal(goal.goal_id).then(setSelected) }}>
              <Card>
                <Card.Header>
                  <div className="flex items-start justify-between gap-2">
                    <Title as="h3">{goal.name}</Title>
                    <div className="shrink-0 whitespace-nowrap"><StatusBadge goal={goal} /></div>
                  </div>
                  {goal.description && <Text>{goal.description}</Text>}
                </Card.Header>
                <Card.Body>
                  <div className="space-y-1">
                    <IndicatorRow ind={goal.primary_metric} />
                    {(goal.impact?.revenue_at_stake ?? 0) > 0 && (
                      <p className="text-xs text-amber-700">
                        ~${goal.impact!.revenue_at_stake.toLocaleString()} en juego
                        {goal.impact!.basis === 'assigned' && <span className="text-muted-foreground"> (estimado)</span>}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2">
                      <Text fontSize="caption" as="span">{goal.actionables_count ?? 0} actionables</Text>
                      <Text fontSize="caption" as="span" className="flex items-center gap-1">Ver detalle <Icon source={<ChevronRightIcon />} /></Text>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Create goal modal */}
      <Modal open={openCreate} onDismiss={() => setOpenCreate(false)} maxWidth="600px">
        <Modal.Header title="Nuevo objetivo" />
        <Modal.Body>
          <div className="w-full">
          <Text>Definí el objetivo y su métrica principal (el número que mira el comerciante).</Text>
          <div className="space-y-3 mt-3">
            <div>
              <Label htmlFor="g-name">Nombre</Label>
              <Input id="g-name" value={gName} placeholder="Ej: Más gente que llega al checkout"
                onChange={(e) => setGName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="g-desc">Descripción</Label>
              <Input id="g-desc" value={gDesc} onChange={(e) => setGDesc(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="g-metric"><Icon source={<StatsIcon />} /> Métrica principal</Label>
              <IndicatorEditor value={gMetric} onChange={setGMetric} ctx={ctx} idPrefix="g-metric" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="g-target">Meta (opcional)</Label>
                <div className="flex gap-1">
                  <Input id="g-target" type="number" value={gTarget} placeholder="20"
                    onChange={(e) => setGTarget(e.target.value)} />
                  <Select id="g-target-type" name="g-target-type" value={gTargetType}
                    onChange={(e) => setGTargetType(e.target.value as any)}>
                    <Select.Option value="relative_pct" label="% de mejora" />
                    <Select.Option value="absolute" label="valor a alcanzar" />
                  </Select>
                </div>
                <Text fontSize="caption" className="mt-1">Distingue "ganar" de "no empeorar".</Text>
              </div>
              <div>
                <Label htmlFor="g-tier">Impacto (para priorizar)</Label>
                <Select id="g-tier" name="g-tier" value={gTier} onChange={(e) => setGTier(e.target.value as any)}>
                  <Select.Option value="high" label="Alto" />
                  <Select.Option value="medium" label="Medio" />
                  <Select.Option value="low" label="Bajo" />
                </Select>
                <Text fontSize="caption" className="mt-1">Usado si la métrica no calcula ingresos.</Text>
              </div>
            </div>
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="neutral" onClick={() => setOpenCreate(false)}>Cancelar</Button>
          <Button appearance="primary" onClick={handleCreate}>Crear objetivo</Button>
        </Modal.Footer>
      </Modal>

      {/* AI assistant modal */}
      <AssistantDialog open={openAssistant} onClose={() => setOpenAssistant(false)}
        onCreated={() => { setOpenAssistant(false); loadGoals() }} />

      {/* Plan-draft review — the put_plan_draft gate (delta-aware; loose hints bound here) */}
      <DraftReviewDialog draft={reviewing} existingGoals={goals} ctx={ctx} onClose={() => setReviewing(null)}
        onResolved={() => { setReviewing(null); loadDrafts(); loadGoals() }} />

      {/* Goal detail modal */}
      <GoalDetail goal={selected} ctx={ctx} onClose={() => setSelected(null)}
        onChanged={() => { loadGoals(); }} onArchive={handleArchive} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Gate-side binding (put_plan_draft): the skill expresses measurement INTENT
// (a loose label or "kind.metric" hint); the binding — Desk domain knowledge —
// is resolved HERE by the consultant with the real catalog. Desk never trusts
// a model-constructed binding it didn't offer itself.
// ---------------------------------------------------------------------------

const DOWN_IS_GOOD_METRICS = new Set(['bouncers_share', 'drop_off_rate'])
// Kinds whose bindings need no reference id — a dotted hint can PRE-SUGGEST these.
const ID_FREE_KINDS = new Set<BindingKind>(['store_funnel', 'search', 'visitor_segment'])

/** Pre-suggestion from a dotted "kind.metric" hint (id-free kinds only); else null. */
function suggestBindingFromHint(raw: string): Indicator | null {
  const dot = raw.indexOf('.')
  if (dot < 1) return null
  const kind = raw.slice(0, dot) as BindingKind
  const metric = raw.slice(dot + 1)
  if (!ID_FREE_KINDS.has(kind) || !(BINDING_METRICS[kind] || []).includes(metric)) return null
  return {
    ...emptyIndicator(),
    label: metricLabel(metric),
    binding: { kind, metric },
    direction: DOWN_IS_GOOD_METRICS.has(metric) ? 'down_is_good' : 'up_is_good',
  }
}

const REQUIRED_REF: Partial<Record<BindingKind, keyof Binding>> = {
  funnel_step: 'step_id', datalake_event: 'event_name', custom_event: 'event_id',
  propensity: 'event_id', cohort: 'cohort_id', product: 'product_id', traffic_channel: 'channel',
}
const bindingComplete = (b: Binding) => {
  const ref = REQUIRED_REF[b.kind]
  return !ref || !!(b as any)[ref]
}

interface LooseHint { key: string; gi: number; ai: number; raw: string; actName: string }
type HintResolution = { mode: 'bind' | 'discard'; ind: Indicator }

/** Collect loose indicator hints (strings / binding-less objects) across the draft. */
function collectLooseHints(goals: PlanDraftGoal[]): LooseHint[] {
  const hints: LooseHint[] = []
  goals.forEach((g, gi) => (g.actionables || []).forEach((a, ai) => (a.indicators || []).forEach((raw: any, ii: number) => {
    if (!(raw && typeof raw === 'object' && raw.binding)) {
      hints.push({ key: `${gi}:${ai}:${ii}`, gi, ai, raw: typeof raw === 'string' ? raw : raw?.label || '(sin nombre)', actName: a.name })
    }
  })))
  return hints
}

/** Find the existing goal a draft entry references: goal_id first, exact name fallback. */
function matchExistingGoal(g: PlanDraftGoal | ProposedGoal, existing: Goal[]): Goal | null {
  const gid = (g as PlanDraftGoal).goal_id
  if (gid) return existing.find(e => e.goal_id === gid) || null
  const name = (g.name || '').trim().toLowerCase()
  return existing.find(e => e.name.trim().toLowerCase() === name) || null
}

/**
 * Shared apply engine, delta-aware. Two paths per draft entry:
 *  - references an EXISTING meta (goal_id or exact name) → attach its actionables
 *    to that goal, never mutate the goal itself (the dominant meeting output);
 *  - new goal with a BOUND primary metric → create goal + actionables.
 * New goals without a bound metric are skipped and reported — never silently dropped.
 * Loose string indicators from meeting drafts coerce to unbound {label} (consultant
 * binds later) instead of sinking the apply.
 */
async function applyProposedPlan(plan: (ProposedGoal | PlanDraftGoal)[], storeId: string, existingGoals: Goal[] = []) {
  let provisioned = 0, failed = 0, created = 0, attached = 0, actionablesCreated = 0
  const skipped: string[] = []
  const droppedIndicators: string[] = []

  const createActionablesFor = async (goalId: string, actionables: any[]) => {
    for (const a of actionables) {
      const keyMap: Record<string, { kind: string; ref_id: string }> = {}
      const provisions: any[] = []
      for (const np of (a.new_primitives || [])) {
        try {
          let ref_id = ''
          if (np.kind === 'cohort') {
            const c = await cohortService.createCohort(np.spec)
            ref_id = c.cohort_id
          } else {
            const res = await createCustomEvent({ ...np.spec, store_id: storeId })
            ref_id = res.event.event_id
          }
          keyMap[np.key] = { kind: np.kind, ref_id }
          provisions.push({ kind: np.kind, ref_id, status: 'created', spec: np.spec })
          provisioned++
        } catch (e) {
          provisions.push({ kind: np.kind, ref_id: null, status: 'failed', spec: np.spec })
          failed++
        }
      }
      // Loose meeting hints (strings / binding-less objects) can't persist — the backend
      // requires a binding per indicator. DROP them here and report (never sink the apply,
      // never pretend they were created); the consultant binds a real indicator later.
      const indicators = (a.indicators || [])
        .filter((raw: any) => {
          const bound = raw && typeof raw === 'object' && raw.binding
          if (!bound) droppedIndicators.push(`${a.name}: ${typeof raw === 'string' ? raw : raw?.label || '(sin nombre)'}`)
          return bound
        })
        .map((ind: any) => {
        const b = ind.binding
        const created2 = b.ref_status === 'pending_new' && b.new_primitive_key ? keyMap[b.new_primitive_key] : null
        if (created2) {
          const resolved: any = { ...b, ref_status: 'existing' }
          if (created2.kind === 'cohort') resolved.cohort_id = created2.ref_id
          else resolved.event_id = created2.ref_id
          return { ...ind, binding: resolved, source: 'ai_assisted' as const }
        }
        return { ...ind, source: 'ai_assisted' as const }
      })
      await goalService.createActionable(goalId, {
        name: a.name, intent: a.intent, type: a.type, indicators, provisions,
        ...(a.evidence ? { evidence: a.evidence } : {}),
      } as any)
      actionablesCreated++
    }
  }

  for (const g of plan) {
    const existing = matchExistingGoal(g, existingGoals)
    if (existing) {
      await createActionablesFor(existing.goal_id, g.actionables || [])
      attached++
      continue
    }
    if (!g.primary_metric?.binding?.kind) { skipped.push(g.name); continue }
    const primary = await withGa4Baseline({ ...g.primary_metric, source: 'ai_assisted' }, storeId)
    const goal = await goalService.createGoal({
      name: g.name, description: g.description, primary_metric: primary,
      target: g.target || undefined, impact_tier: g.impact_tier,
      ...((g as PlanDraftGoal).evidence ? { evidence: (g as PlanDraftGoal).evidence } : {}),
    } as any)
    await createActionablesFor(goal.goal_id, g.actionables || [])
    created++
  }
  return { created, attached, actionablesCreated, provisioned, failed, skipped, droppedIndicators }
}

/**
 * Plan-draft review — THE gate for put_plan_draft (spec §2.1: the MCP write only
 * persists; nothing activates until the consultant applies HERE). What's rendered
 * is the persisted plan_doc itself — WYSIWYG: reviewed object === applied object.
 */
function DraftReviewDialog({ draft, existingGoals, ctx, onClose, onResolved }: {
  draft: PlanDraft | null; existingGoals: Goal[]; ctx: PickerContext; onClose: () => void; onResolved: () => void
}) {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)
  // Loose-hint resolutions: keyed `${gi}:${ai}:${ii}`. Default = bind-with-suggestion
  // when the hint parses (id-free kinds); otherwise discard — the gate never invents
  // a binding, the consultant opts in.
  const [resolutions, setResolutions] = useState<Record<string, HintResolution>>({})
  useEffect(() => {
    if (!draft) return
    const init: Record<string, HintResolution> = {}
    for (const h of collectLooseHints(draft.plan_doc?.goals || [])) {
      const suggestion = suggestBindingFromHint(h.raw)
      init[h.key] = suggestion
        ? { mode: 'bind', ind: suggestion }
        : { mode: 'discard', ind: { ...emptyIndicator(), label: h.raw } }
    }
    setResolutions(init)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.draft_id])

  if (!draft) return null
  const doc = draft.plan_doc || { goals: [] }
  const preguntas = doc.preguntas || []
  const looseHints = collectLooseHints(doc.goals)
  const hintsFor = (gi: number, ai: number) => looseHints.filter(h => h.gi === gi && h.ai === ai)
  // Delta triage: existing-meta entries attach actionables; the rest create (bound) or skip.
  const matched = (g: (typeof doc.goals)[number]) => matchExistingGoal(g, existingGoals)
  const unbound = doc.goals.filter(g => !matched(g) && !g.primary_metric?.binding?.kind)
  const newGoals = doc.goals.filter(g => !matched(g) && g.primary_metric?.binding?.kind)
  const deltaCount = doc.goals.length - unbound.length - newGoals.length
  const totalActs = doc.goals.filter(g => matched(g) || g.primary_metric?.binding?.kind)
    .reduce((n, g) => n + (g.actionables || []).length, 0)

  const apply = async () => {
    // Guard: every hint bound by the consultant must have its required ref chosen —
    // fail HERE, before anything is created, never mid-apply.
    const incomplete = looseHints.filter(h => resolutions[h.key]?.mode === 'bind' && !bindingComplete(resolutions[h.key].ind.binding))
    if (incomplete.length) {
      toast({ title: 'Faltan referencias en indicadores bindeados', description: incomplete.map(h => `"${h.raw}" (${h.actName})`).join(', '), variant: 'destructive' })
      return
    }
    setBusy(true)
    const storeId = authService.getCurrentStoreId() || ''
    try {
      // Resolve loose hints into the plan the gate showed: bound hints become real
      // indicators; discarded ones are removed DELIBERATELY (recorded below).
      const discarded: string[] = []
      const resolvedGoals = doc.goals.map((g, gi) => ({
        ...g,
        actionables: (g.actionables || []).map((a, ai) => ({
          ...a,
          indicators: (a.indicators || []).flatMap((raw: any, ii: number) => {
            if (raw && typeof raw === 'object' && raw.binding) return [raw]
            const r = resolutions[`${gi}:${ai}:${ii}`]
            if (r?.mode === 'bind') return [r.ind]
            discarded.push(`${a.name}: ${typeof raw === 'string' ? raw : raw?.label || '(sin nombre)'}`)
            return []
          }),
        })),
      }))
      const r = await applyProposedPlan(resolvedGoals, storeId, existingGoals)
      let qCreated = 0
      for (const p of preguntas) {
        try {
          await questionService.create({
            store_id: storeId, question: p.question, notes: p.notes,
            risk_level: p.risk_level, indicator: p.indicator,
            evidence: draft.meeting_context ? { summary: `De la reunión: ${draft.meeting_context}` } : undefined,
          } as any)
          qCreated++
        } catch { /* per-question failure shouldn't sink the apply; reported below */ }
      }
      // Resolution note = the honest delta record (skips feed the Ola 5 dataset).
      const noteParts = []
      if (r.skipped.length) noteParts.push(`sin match ni métrica bindeada (no creados): ${r.skipped.join('; ')}`)
      if (discarded.length) noteParts.push(`indicadores descartados en revisión (decisión consultora): ${discarded.join('; ')}`)
      if (r.droppedIndicators.length) noteParts.push(`indicadores sueltos sin binding (backstop): ${r.droppedIndicators.join('; ')}`)
      if (qCreated < preguntas.length) noteParts.push(`${preguntas.length - qCreated} pregunta(s) fallaron`)
      await goalService.resolvePlanDraft(draft.draft_id, 'applied', noteParts.join(' · ') || undefined)
      toast({
        title: `Borrador aplicado: ${r.actionablesCreated} accionable(s) (${r.attached} meta(s) existentes, ${r.created} nueva(s)), ${qCreated} pregunta(s)`,
        description: r.skipped.length ? `Sin aplicar (sin match ni métrica): ${r.skipped.join(', ')}` : undefined,
        ...(r.skipped.length ? { variant: 'destructive' as const } : {}),
      })
      onResolved()
    } catch (e: any) {
      toast({ title: 'Error al aplicar el borrador', description: e?.message, variant: 'destructive' })
    } finally { setBusy(false) }
  }

  const dismiss = async () => {
    setBusy(true)
    try {
      await goalService.resolvePlanDraft(draft.draft_id, 'dismissed')
      toast({ title: 'Borrador descartado', description: 'Queda registrado como no aplicado.' })
      onResolved()
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message, variant: 'destructive' })
    } finally { setBusy(false) }
  }

  return (
    <Modal open={!!draft} onDismiss={() => onClose()} maxWidth="680px">
      <Modal.Header title="Borrador de plan (de reunión)" />
      <Modal.Body>
        {/* Same scroll pattern as GoalDetail: long drafts (quincenal deltas) need it */}
        <div className="w-full max-h-[75vh] overflow-y-auto space-y-3 px-1 py-2" style={{ scrollbarGutter: 'stable both-edges' }}>
          <Text fontSize="caption">
            Propuesto {draft.proposed_by ? `por ${draft.proposed_by} ` : ''}el {new Date(draft.created_at).toLocaleDateString('es-AR')}
            {draft.meeting_context ? ` · ${draft.meeting_context}` : ''}
          </Text>
          <Text>Nada de esto existe todavía — revisá y aplicá (o descartá). Este es el gate.</Text>
          {doc.goals.map((g, gi) => (
            <Card key={gi}>
              <Card.Header>
                <div className="flex items-center justify-between gap-2">
                  <Title as="h4">{g.name}</Title>
                  {matched(g)
                    ? <Tag appearance="primary">meta existente · +{(g.actionables || []).length} accionables</Tag>
                    : !g.primary_metric?.binding?.kind && <Tag appearance="danger">sin match ni métrica</Tag>}
                </div>
                {g.description && <Text>{g.description}</Text>}
              </Card.Header>
              <Card.Body>
                <div className="space-y-2 text-sm">
                  {g.primary_metric && (
                    <div className="flex items-center gap-2">
                      <Tag appearance="neutral">Métrica</Tag>
                      <Text as="span">{g.primary_metric.label || g.primary_metric.binding?.metric || '(a definir)'}</Text>
                    </div>
                  )}
                  {g.evidence?.summary && <Text fontSize="caption">{g.evidence.summary}</Text>}
                  {(g.actionables || []).map((a, ai) => (
                    <div key={ai} className="rounded border p-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <Text as="span" color="neutral-textHigh">{a.name}</Text>
                        <Tag appearance="neutral">{a.type}</Tag>
                      </div>
                      {a.intent && <Text fontSize="caption">{a.intent}</Text>}
                      {/* Gate-side binding: the skill sent intent; the instrument is chosen HERE */}
                      {hintsFor(gi, ai).map((h) => {
                        const r = resolutions[h.key]
                        if (!r) return null
                        return (
                          <div key={h.key} className="rounded border border-amber-300 p-2 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <Text fontSize="caption" color="neutral-textHigh">Indicador propuesto: "{h.raw}"</Text>
                              {/* State tag + ONE action button that always flips state — never an inert control */}
                              <div className="flex gap-1 items-center shrink-0">
                                {r.mode === 'bind' ? (
                                  <>
                                    <Tag appearance="success">se creará al aplicar</Tag>
                                    <Button appearance="neutral"
                                      onClick={() => setResolutions({ ...resolutions, [h.key]: { ...r, mode: 'discard' } })}>
                                      Descartar
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Tag appearance="neutral">descartado</Tag>
                                    <Button appearance="primary"
                                      onClick={() => setResolutions({ ...resolutions, [h.key]: { ...r, mode: 'bind' } })}>
                                      Bindear
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                            {r.mode === 'bind' ? (
                              <IndicatorEditor value={r.ind} ctx={ctx} idPrefix={`hint-${h.key.replace(/:/g, '-')}`}
                                onChange={(ind) => setResolutions({ ...resolutions, [h.key]: { ...r, ind } })} />
                            ) : (
                              <Text fontSize="caption">No se creará — queda registrado como descartado en revisión.</Text>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          ))}
          {preguntas.length > 0 && (
            <Card>
              <Card.Header title={`Preguntas propuestas (${preguntas.length})`} />
              <Card.Body>
                <div className="space-y-1">
                  {preguntas.map((p, pi) => <Text key={pi} fontSize="caption">• {p.question}</Text>)}
                </div>
              </Card.Body>
            </Card>
          )}
          {unbound.length > 0 && (
            <Text fontSize="caption" color="danger-textHigh">
              ⚠ {unbound.length} entrada(s) sin meta existente que matchee ni métrica bindeada — NO se aplicarán, quedan anotadas en el borrador.
            </Text>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance="neutral" onClick={dismiss} disabled={busy}>Descartar</Button>
        <Button appearance="primary" onClick={apply} disabled={busy}>
          {busy ? <Spinner size="small" /> : `Aplicar (${totalActs} accionables${deltaCount ? ` en ${deltaCount} metas existentes` : ''}${newGoals.length ? `, ${newGoals.length} metas nuevas` : ''}${preguntas.length ? `, ${preguntas.length} preguntas` : ''})`}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function AssistantDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void
}) {
  const { toast } = useToast()
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [plan, setPlan] = useState<ProposedGoal[] | null>(null)

  const generate = async () => {
    if (!text.trim()) { toast({ title: 'Describí los objetivos primero', variant: 'destructive' }); return }
    setLoading(true); setPlan(null)
    try {
      const res = await goalService.decompose(text)
      setPlan(res.plan?.goals || [])
      if (!res.plan?.goals?.length) toast({ title: 'El asistente no propuso objetivos. Probá con más detalle.' })
    } catch (e: any) {
      toast({ title: 'Error del asistente', description: e?.message, variant: 'destructive' })
    } finally { setLoading(false) }
  }

  const createAll = async () => {
    if (!plan) return
    setCreating(true)
    const storeId = authService.getCurrentStoreId() || ''
    try {
      const r = await applyProposedPlan(plan, storeId)
      toast({
        title: `${r.created} objetivo(s) creado(s)`,
        description: r.provisioned ? `${r.provisioned} cohorte(s)/evento(s) creados${r.failed ? `, ${r.failed} fallaron` : ''}.` : undefined,
      })
      setText(''); setPlan(null)
      onCreated()
    } catch (e: any) {
      toast({ title: 'Error al crear', description: e?.message, variant: 'destructive' })
    } finally { setCreating(false) }
  }

  const newPrimitiveCount = (plan || []).reduce((n, g) =>
    n + (g.actionables || []).reduce((m, a) => m + (a.new_primitives?.length || 0), 0), 0)

  return (
    <Modal open={open} onDismiss={() => onClose()} maxWidth="680px">
      <Modal.Header title="Asistente de objetivos" />
      <Modal.Body>
        <div className="w-full">
        <Text>
          Describí en tus palabras qué querés lograr con la tienda este trimestre. El asistente propone objetivos,
          actionables e indicadores conectados a los datos reales de la tienda. Vos revisás y aprobás.
        </Text>

        <div className="space-y-3 mt-3">
          <Textarea id="assistant-text" autoGrow minLines={4} maxLines={16}
            placeholder="Ej: Quiero que más gente llegue al checkout, recuperar conversión en mobile, y mover el inventario que no se vende."
            value={text} onChange={(e) => setText(e.target.value)} disabled={loading || creating}
          />

          {plan && plan.length > 0 && (
            <div className="space-y-3 border-t pt-3">
              <Text color="neutral-textHigh">Plan propuesto ({plan.length} objetivos) — revisá antes de crear:</Text>
              {plan.map((g, gi) => (
                <Card key={gi}>
                  <Card.Header>
                    <Title as="h4">{g.name}</Title>
                    {g.description && <Text>{g.description}</Text>}
                  </Card.Header>
                  <Card.Body>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Tag appearance="neutral">Métrica</Tag>
                        <Text as="span">{g.primary_metric.label || g.primary_metric.binding?.metric}</Text>
                      </div>
                      {(g.actionables || []).map((a, ai) => (
                        <div key={ai} className="rounded border p-2">
                          <div className="flex items-center justify-between">
                            <Text as="span" color="neutral-textHigh">{a.name}</Text>
                            <Tag appearance="neutral">{a.type}</Tag>
                          </div>
                          {a.intent && <Text fontSize="caption">{a.intent}</Text>}
                          {(a.indicators || []).map((ind, ii) => (
                            <Text key={ii} fontSize="caption">• {ind.label || ind.binding?.metric}</Text>
                          ))}
                          {(a.new_primitives || []).map((np, ni) => (
                            <p key={ni} className="text-xs text-amber-600">⚙ se creará {np.kind === 'cohort' ? 'cohorte' : 'evento'}: {np.spec?.name || np.spec?.display_name || np.spec?.event_name || ''}</p>
                          ))}
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              ))}
              {newPrimitiveCount > 0 && (
                <p className="text-xs text-amber-600">
                  Al aprobar, el asistente creará {newPrimitiveCount} cohorte(s)/evento(s) nuevos y conectará los indicadores automáticamente. Empiezan a juntar datos desde ese momento (estado "calibrando" hasta tener línea de base).
                </p>
              )}
            </div>
          )}
        </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance="neutral" onClick={onClose} disabled={creating}>Cerrar</Button>
        <Button appearance={plan && plan.length > 0 ? 'neutral' : 'primary'}
          onClick={generate} disabled={loading || creating}>
          {loading ? <><Spinner size="small" /> Generando…</> : <>Generar plan</>}
        </Button>
        {plan && plan.length > 0 && (
          <Button appearance="primary" onClick={createAll} disabled={creating}>
            {creating ? <><Spinner size="small" /> Creando…</> : <>Crear todos</>}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

function GoalDetail({ goal, ctx, onClose, onChanged, onArchive }: {
  goal: Goal | null; ctx: PickerContext; onClose: () => void
  onChanged: () => void; onArchive: (g: Goal) => void
}) {
  const { toast } = useToast()
  const [addOpen, setAddOpen] = useState(false)
  const [aName, setAName] = useState('')
  const [aType, setAType] = useState<Actionable['type']>('change')
  const [aIntent, setAIntent] = useState('')
  const [aEvidence, setAEvidence] = useState('')
  const [aInd, setAInd] = useState<Indicator>(emptyIndicator())

  if (!goal) return null

  const addActionable = async () => {
    if (!aName) { toast({ title: 'Poné un nombre', variant: 'destructive' }); return }
    try {
      await goalService.createActionable(goal.goal_id, {
        name: aName, intent: aIntent, type: aType,
        indicators: aInd.label ? [aInd] : [],
        ...(aEvidence.trim() ? { evidence: { summary: aEvidence.trim() } } : {}),
      })
      toast({ title: 'Actionable agregado' })
      setAddOpen(false); setAName(''); setAIntent(''); setAEvidence(''); setAInd(emptyIndicator())
      onChanged()
      // refresh detail
      const fresh = await goalService.getGoal(goal.goal_id)
      Object.assign(goal, fresh)
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message, variant: 'destructive' })
    }
  }

  // Ship pivot (FW-1 D1): marking the change as shipped stamps activated_at server-side — the
  // before/after anchor its mechanism indicators measure around.
  const markShipped = async (a: Actionable) => {
    try {
      await goalService.updateActionable(a.actionable_id, { status: 'active' })
      toast({ title: 'Marcado como aplicado', description: 'Midiendo desde ahora.' })
      onChanged()
      const fresh = await goalService.getGoal(goal.goal_id)
      Object.assign(goal, fresh)
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message, variant: 'destructive' })
    }
  }

  // Descartar = "no se va a hacer" — a program decision, not a delete: the row keeps
  // status 'dismissed' and the iteration export reports it as 'descartado' (never hidden).
  const discardActionable = async (a: Actionable) => {
    try {
      await goalService.dismissActionable(a.actionable_id)
      toast({ title: 'Actionable descartado', description: 'Queda registrado como no ejecutado.' })
      onChanged()
      const fresh = await goalService.getGoal(goal.goal_id)
      Object.assign(goal, fresh)
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message, variant: 'destructive' })
    }
  }

  const TYPE_LABELS: Record<Actionable['type'], string> = {
    change: 'Cambio', observe: 'Observar', instrument: 'Instrumentar', nudge: 'Sugerencia', experiment: 'Experimento',
  }

  return (
    <Modal open={!!goal} onDismiss={() => onClose()} maxWidth="880px">
      <Modal.Header title={`${goal.status_light} ${goal.name}`} />
      <Modal.Body>
        <div className="w-full max-h-[75vh] overflow-y-auto px-1 py-2" style={{ scrollbarGutter: 'stable both-edges' }}>
        <div className="flex items-start justify-between gap-3">
          {goal.description ? <Text>{goal.description}</Text> : <span />}
          <div className="shrink-0">
            <Button appearance="transparent" onClick={() => onArchive(goal)}>
              <Icon source={<TrashIcon />} color="currentColor" />
            </Button>
          </div>
        </div>

        <div className="space-y-4 mt-3">
          <Card>
            <Card.Header title="Métrica principal" />
            <Card.Body><IndicatorRow ind={goal.primary_metric} /></Card.Body>
          </Card>

          <div className="flex items-center justify-between">
            <Title as="h3">Actionables</Title>
            <Button appearance="neutral" onClick={() => setAddOpen(true)}>
              <Icon source={<PlusIcon />} color="currentColor" /> Agregar
            </Button>
          </div>

          {(goal.actionables || []).length === 0 ? (
            <Text>Sin actionables todavía.</Text>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
              {(goal.actionables || []).map((a) => (
                <Card key={a.actionable_id}>
                  <Card.Header>
                    <div className="flex items-center justify-between gap-2">
                      <Title as="h4">{a.name}</Title>
                      <div className="flex items-center gap-1 shrink-0">
                        <Tag appearance="neutral">{TYPE_LABELS[a.type] ?? a.type}</Tag>
                        {a.activated_at
                          ? <Tag appearance="success">Aplicado</Tag>
                          : a.status === 'proposed' && <Tag appearance="neutral">Propuesto</Tag>}
                      </div>
                    </div>
                    {a.intent && <Text>{a.intent}</Text>}
                  </Card.Header>
                  <Card.Body>
                    <div className="space-y-2">
                      {/* Evidence (FW-2): why this action exists — the plan's "Detalle" */}
                      {a.evidence?.summary && (
                        <div className="rounded border-l-2 border-neutral-300 pl-2">
                          <Text fontSize="caption">{a.evidence.summary}</Text>
                          {(a.evidence.metrics || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {a.evidence.metrics!.map((m, i) => (
                                <Tag key={i} appearance="neutral">{m.label}: {m.value}</Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="space-y-1">
                        {(a.indicators || []).map((ind, i) => <IndicatorRow key={i} ind={ind} />)}
                      </div>
                      {/* Ship pivot: measure before/after around activated_at, not created_at */}
                      {a.activated_at ? (
                        <Text fontSize="caption">
                          Midiendo desde {new Date(a.activated_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                        </Text>
                      ) : (a.status === 'proposed' || a.status === 'paused') && (
                        <div className="flex items-center gap-2">
                          <Button appearance="neutral" onClick={() => markShipped(a)}>
                            Marcar como aplicado
                          </Button>
                          <Button appearance="transparent" onClick={() => discardActionable(a)}>
                            Descartar
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
        </div>
      </Modal.Body>

      {/* Add actionable */}
      <Modal open={addOpen} onDismiss={() => setAddOpen(false)} maxWidth="560px">
        <Modal.Header title="Nuevo actionable" />
        <Modal.Body>
          <div className="w-full">
          <div className="space-y-3">
            <div>
              <Label htmlFor="a-name">Nombre</Label>
              <Input id="a-name" value={aName} onChange={(e) => setAName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="a-type">Tipo</Label>
                <Select id="a-type" name="a-type" value={aType}
                  onChange={(e) => setAType(e.target.value as Actionable['type'])}>
                  <Select.Option value="change" label="Cambio" />
                  <Select.Option value="observe" label="Observar" />
                  <Select.Option value="instrument" label="Instrumentar" />
                  <Select.Option value="nudge" label="Sugerencia" />
                  <Select.Option value="experiment" label="Experimento" />
                </Select>
              </div>
              <div>
                <Label htmlFor="a-intent">Intención</Label>
                <Input id="a-intent" value={aIntent} onChange={(e) => setAIntent(e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="a-evidence">Evidencia (opcional)</Label>
              <Input id="a-evidence" value={aEvidence}
                onChange={(e) => setAEvidence(e.target.value)}
                placeholder="El dato que motiva esta acción, ej: solo 21% de HP entra a categoría" />
            </div>
            <div>
              <Label htmlFor="a-ind">Indicador (opcional)</Label>
              <IndicatorEditor value={aInd} onChange={setAInd} ctx={ctx} idPrefix="a-ind" />
            </div>
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="neutral" onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button appearance="primary" onClick={addActionable}>Agregar</Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}
